import api from 'api';
import {get} from 'lodash';
import {useMemo, useState} from 'react';
import toast from 'react-hot-toast';
import {useMutation, useQuery} from 'react-query';
import {BlogResponse, ListResponse} from 'types';
import {SelectOption} from 'types/ui';

async function getMessageTemplates() {
  const {data} = await api.get<ListResponse<BlogResponse>>(`/support/blogs/`, {
    params: {content_type: 'template'},
  });
  return data?.results;
}
type SendBulkMessagePayload = {
  customer_ids: number[];
  message: string;
};

async function sendBulkMessage({
  customer_ids,
  message,
}: SendBulkMessagePayload) {
  const {data} = await api.post('/customers/sms/blast/', {
    customer_ids,
    message,
  });
  return data;
}

export const useSendBulkMessage = ({
  onClose,
  customer_ids,
}: {
  onClose: () => void;
  customer_ids: number[];
}) => {
  const [message, setMessage] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState<SelectOption>();
  const {data: messageTemplates} = useQuery(
    ['message_templates'],
    getMessageTemplates,
    {
      refetchOnWindowFocus: false,
    }
  );

  const {mutateAsync: sendBulkMessageMutation, isLoading: isSending} =
    useMutation({
      mutationFn: sendBulkMessage,
      onSuccess: () => {
        toast.success('Message sent successfully');
        handleClose();
      },
      onError: (error) => {
        const errorMessage = get(
          error,
          'response.data.detail',
          'Something went wrong'
        );
        toast.error(errorMessage);
      },
    });

  const handleSendBulkMessage = async () => {
    const uniqueCustomerIds = Array.from(new Set(customer_ids));
    if (uniqueCustomerIds.length <= 0) {
      toast.error('Please select at least one customer');
      return;
    }
    await sendBulkMessageMutation({
      customer_ids: uniqueCustomerIds,
      message,
    });
  };

  const handleClose = () => {
    onClose();
    setMessage('');
    setSelectedTemplate(undefined);
  };
  const templateOptions = useMemo(() => {
    return messageTemplates?.map((temp) => ({
      label: temp?.title,
      value: temp?.content,
    }));
  }, [messageTemplates]);
  return {
    message,
    setMessage,
    selectedTemplate,
    setSelectedTemplate,
    templateOptions,
    handleSendBulkMessage,
    isSending,
    handleClose,
  };
};
